<script setup lang="ts">
  import { computed, getCurrentInstance, nextTick, ref } from 'vue';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import V2SetupPickerList from '@/components/v2/store-setup/shared/V2SetupPickerList.vue';
  import { regionName } from '@/domain/store-setup/display-names';
  import { countryOptions } from '@/domain/store-setup/location-step';

  /**
   * «País: Argentina» (plan §4.1): a chip opening a searchable list of the 16
   * supported countries, named and sorted in the UI language.
   */
  const props = withDefaults(
    defineProps<{
      value?: string | null;
      disabled?: boolean;
    }>(),
    { value: null, disabled: false }
  );
  const emit = defineEmits<{ (e: 'input', code: string): void }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const anchor = ref<HTMLElement | null>(null);

  const locale = computed(() => proxy.$i18n.locale);
  const options = computed(() => countryOptions(locale.value));
  const label = computed(() =>
    props.value
      ? tr('storeSetup.location.country', {
          country: regionName(props.value, locale.value),
        })
      : tr('storeSetup.location.chooseCountry')
  );

  async function pick(code: string, close: () => void) {
    close();
    if (code !== props.value) emit('input', code);
    // The focused search box went away with the panel: give focus back to the chip.
    await nextTick();
    const chip = anchor.value?.querySelector('button');
    if (chip instanceof HTMLElement) chip.focus({ preventScroll: true });
  }
</script>

<template>
  <div ref="anchor" class="v2-setup-country-chip">
    <PosUiPopover placement="bottom" align="start" :disabled="disabled">
      <template #trigger="{ open, toggle }">
        <PosUiChip
          class="country-trigger"
          icon="map-marker"
          :disabled="disabled"
          aria-haspopup="listbox"
          :aria-expanded="open ? 'true' : 'false'"
          @click="toggle"
        >
          <span class="country-label">{{ label }}</span>
          <PosUiIcon name="chevron-down" class="country-caret" />
        </PosUiChip>
      </template>
      <template #default="{ close }">
        <V2SetupPickerList
          :options="options"
          :value="value"
          :search-label="$t('posV2.search')"
          :list-label="$t('storeSetup.location.chooseCountry')"
          :empty-text="$t('posV2.noResults')"
          @select="(code) => pick(code, close)"
        />
      </template>
    </PosUiPopover>
  </div>
</template>

<style scoped lang="scss">
  .v2-setup-country-chip {
    @apply tw-flex tw-min-w-0;
    .country-trigger {
      @apply tw-max-w-full;
    }
    .country-label {
      @apply tw-min-w-0 tw-truncate;
    }
    .country-caret {
      // Margin, not the chip's flex gap (old POS hardware, Chrome < 84).
      @apply tw-ml-1.5 tw-shrink-0 tw-text-base tw-text-neutral-500;
    }
  }
</style>
